'use client';

import clsx from 'clsx';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageChanger from '@/i18n/components/language-changer';
import TopMenu4Mobile from './top-menu-mobile';
import Link from 'next/link';
import MenuDropdownButton from './top-menu-dropdown-button';
import Logo from './logo';
import TopMenuPopover from './top-menu-popover';
import { FooterButton } from '@/components/ui/common/buttons';
import useContactUsContext from '@/components/providers/context/contact-us-context';
import { useLocale } from '@/components/providers/context/locale-context';
import { client } from '@/sanity/lib/client';
import { BLOG_MENU_POSTS_QUERY } from '@/sanity/lib/queries';
import LoadingPlaceholder from '@/components/ui/common/loading-placeholder';
import { preloadMenuImage } from './menu-image-cache';

// ─── 类型 ────────────────────────────────────────────────────────────────────

export type SubMenu = {
    title: string;
    href: string;
    index: number;
    isScrollTo?: boolean;
};
export type MenuDetail = {
    title: string;
    subtitle: string;
    imageUrl: string;
    imageDesc: string;
};

type PopoverMenuConfig = {
    key: string;
    /** i18n key：菜单项标题 */
    titleKey: string;
    /** i18n key：popover 标题，为空则不渲染 popover */
    popoverTitleKey?: string;
    /** i18n key：子菜单列表 */
    subMenusKey?: string;
    /** i18n key：菜单详情列表，dynamicDetails=true 时忽略，改从 Sanity 动态拉取 */
    menuDetailsKey?: string;
    /** 是否从 Sanity 动态获取 menuDetails */
    dynamicDetails?: boolean;
};

/** 模块级缓存，避免重复请求（key = `${menuKey}:${locale}`） */
const blogMenuCache: Record<string, MenuDetail[]> = {};
const blogMenuRequestCache: Record<string, Promise<MenuDetail[]>> = {};
const EMPTY_MENU_DETAIL: MenuDetail = {
    title: '',
    subtitle: '',
    imageUrl: '',
    imageDesc: '',
};

// ─── 常量（组件外，避免每次渲染重建）──────────────────────────────────────────

/** 调试开关：设为菜单 key（如 'product'）可持续展开对应 popover，上线前改回 null */
const DEBUG_POPOVER: string | null = null;
const POPOVER_ANIMATION_MS = 140;
const DESKTOP_POPOVER_HEIGHT_CLASS = 'h-[406px]';

/** 所有顶部菜单项配置，增删菜单只需修改此数组 */
const POPOVER_MENUS: PopoverMenuConfig[] = [
    {
        key: 'product',
        titleKey: 'product',
        popoverTitleKey: 'productTitle',
        subMenusKey: 'productSubMenus',
        menuDetailsKey: 'productMenuDetails',
    },
    {
        key: 'blog',
        titleKey: 'blog',
        popoverTitleKey: 'blogTitle',
        subMenusKey: 'blogSubMenus',
        dynamicDetails: true, // menuDetails 从 Sanity 动态拉取
    },
    {
        key: 'company',
        titleKey: 'company',
        popoverTitleKey: 'companyTitle',
        subMenusKey: 'companySubMenus',
        menuDetailsKey: 'companyMenuDetails',
    },
];

function getBlogMenuCacheKey(locale: string) {
    return `blog:${locale}`;
}

function toBlogMenuDetail(post: {
    title: string;
    description: string;
    imageUrl: string;
}) {
    return {
        title: post.title ?? '',
        subtitle: post.description ?? '',
        imageUrl: post.imageUrl ?? '',
        imageDesc: post.title ?? '',
    };
}

function normalizeBlogMenuDetails(
    posts: {
        title: string;
        description: string;
        slug: string;
        type: string;
        imageUrl: string;
        publishedAt: string;
    }[],
) {
    const newest = [...posts].sort((a, b) =>
        (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''),
    )[0];
    const byType = (type: string) => posts.find((post) => post.type === type);

    return [
        newest ? toBlogMenuDetail(newest) : EMPTY_MENU_DETAIL,
        byType('news') ? toBlogMenuDetail(byType('news')!) : EMPTY_MENU_DETAIL,
        byType('research')
            ? toBlogMenuDetail(byType('research')!)
            : EMPTY_MENU_DETAIL,
        byType('weekly')
            ? toBlogMenuDetail(byType('weekly')!)
            : EMPTY_MENU_DETAIL,
    ];
}

function fetchBlogMenuDetails(locale: string) {
    const cacheKey = getBlogMenuCacheKey(locale);

    if (blogMenuCache[cacheKey]) {
        return Promise.resolve(blogMenuCache[cacheKey]);
    }

    if (!blogMenuRequestCache[cacheKey]) {
        blogMenuRequestCache[cacheKey] = client
            .fetch<
                {
                    title: string;
                    description: string;
                    slug: string;
                    type: string;
                    imageUrl: string;
                    publishedAt: string;
                }[]
            >(BLOG_MENU_POSTS_QUERY, { lang: locale })
            .then((posts) => {
                const details = normalizeBlogMenuDetails(posts);
                blogMenuCache[cacheKey] = details;
                details.forEach((detail) => {
                    preloadMenuImage(detail.imageUrl);
                });
                return details;
            })
            .finally(() => {
                delete blogMenuRequestCache[cacheKey];
            });
    }

    return blogMenuRequestCache[cacheKey];
}

// ─── 组件 ─────────────────────────────────────────────────────────────────────

function TopMenu({ textColor }: Readonly<{ textColor: 'black' | 'white' }>) {
    const { t } = useTranslation('menu');
    const locale = useLocale();
    const { openForm, closeForm } = useContactUsContext();
    const closeFormRef = useRef(closeForm);
    closeFormRef.current = closeForm;

    const [openPopover, setOpenPopover] = useState<string | null>(
        DEBUG_POPOVER,
    );
    const [languageSelectorOpen, setLanguageSelectorOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [blogMenuDetails, setBlogMenuDetails] = useState<MenuDetail[]>([]);
    const [isBlogMenuLoading, setIsBlogMenuLoading] = useState(false);

    const menuActive = openPopover !== null || scrolled || languageSelectorOpen;

    // 当前激活的菜单配置（只有配置了 popoverTitleKey 的才算真正展开）
    const activeMenu =
        POPOVER_MENUS.find(
            (m) => m.key === openPopover && !!m.popoverTitleKey,
        ) ?? null;

    // 延迟卸载：关闭时保留内容直到 slide-out 动画播完
    const [renderMenu, setRenderMenu] = useState<PopoverMenuConfig | null>(
        null,
    );
    useEffect(() => {
        if (activeMenu) {
            setRenderMenu(activeMenu);
        } else {
            const timer = setTimeout(
                () => setRenderMenu(null),
                POPOVER_ANIMATION_MS,
            );
            return () => clearTimeout(timer);
        }
    }, [activeMenu]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            void fetchBlogMenuDetails(locale).catch((error) => {
                console.error(error);
            });
        }, 150);

        return () => window.clearTimeout(timer);
    }, [locale]);

    useEffect(() => {
        POPOVER_MENUS.forEach((menu) => {
            if (!menu.menuDetailsKey || menu.dynamicDetails) {
                return;
            }

            const details = t(menu.menuDetailsKey, {
                returnObjects: true,
            }) as MenuDetail[];

            details.forEach((detail) => {
                preloadMenuImage(detail.imageUrl);
            });
        });
    }, [locale, t]);

    // blog menuDetails：优先命中预热缓存，首次打开时兜底拉取
    useEffect(() => {
        if (openPopover !== 'blog') return;
        const cacheKey = getBlogMenuCacheKey(locale);
        const cachedDetails = blogMenuCache[cacheKey];

        if (cachedDetails) {
            setBlogMenuDetails(cachedDetails);
            setIsBlogMenuLoading(false);
            return;
        }

        let cancelled = false;
        setIsBlogMenuLoading(true);

        void fetchBlogMenuDetails(locale)
            .then((details) => {
                if (cancelled) {
                    return;
                }

                setBlogMenuDetails(details);
                setIsBlogMenuLoading(false);
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }

                setIsBlogMenuLoading(false);
                console.error(error);
            });

        return () => {
            cancelled = true;
        };
    }, [openPopover, locale]);

    const openMenu = (key: string) => {
        setOpenPopover(key);
    };

    const closePopover = () => {
        if (DEBUG_POPOVER) return; // 调试模式下不允许关闭
        setOpenPopover(null);
    };

    useEffect(() => {
        const onScroll = () =>
            setScrolled(document.documentElement.scrollTop > 1);
        onScroll();
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [locale]);

    useEffect(() => {
        closeFormRef.current();
    }, []);

    // ─── 颜色主题（集中管理所有颜色规则，按状态区分）────────────────────────────
    // 三种互斥状态：popover展开 > 激活（滚动/语言选择器） > 默认
    const theme = (() => {
        if (openPopover) {
            // popover 展开时：黑色导航栏
            return {
                navBg: 'bg-black/95',
                navBorder: '[border-bottom:1px_solid_transparent]',
                backdropBlur: '',
                text: 'white',
                logo: 'white',
                navListText: 'text-[#6A696C]',
                menuItemOpen: 'text-white',
                menuItemClosed: 'text-[#6A696C]',
                langHoverBg: '#333333',
                langDropdownBg: 'bg-[#1a1a1a]',
                langDropdownTextColor: 'text-white',
                langDropdownBorder: false,
                langDropdownShadow: false,
                langDropdownItemHoverBg: 'hover:bg-[#333333]',
                contactHoverText: 'black',
            } as const;
        }
        if (menuActive) {
            // 激活状态（滚动 / 语言选择器开）：白底黑字
            return {
                navBg: 'bg-white/95',
                navBorder: '[border-bottom:1px_solid_#dfdfdf]',
                backdropBlur: '',
                text: 'black',
                logo: 'black',
                navListText: 'text-black',
                menuItemOpen: 'text-black',
                menuItemClosed: 'text-black',
                langHoverBg: '#E8E8E8',
                langDropdownBg: 'bg-white',
                langDropdownTextColor: 'text-black',
                langDropdownBorder: true,
                langDropdownShadow: true,
                langDropdownItemHoverBg: 'hover:bg-gray-100',
                contactHoverText: 'white',
            } as const;
        }
        // 默认状态：透明背景，颜色由 textColor prop 决定
        return {
            navBg: 'bg-transparent',
            navBorder: '[border-bottom:1px_solid_transparent]',
            backdropBlur: '',
            text: textColor,
            logo: textColor,
            navListText: `text-${textColor}`,
            menuItemOpen: `text-${textColor}`,
            menuItemClosed: `text-${textColor}`,
            langHoverBg: textColor === 'black' ? 'white' : '#39383B',
            langDropdownBg: 'bg-white',
            langDropdownTextColor: 'text-black',
            langDropdownBorder: true,
            langDropdownShadow: true,
            langDropdownItemHoverBg: 'hover:bg-gray-100',
            contactHoverText: textColor === 'black' ? '#FFFFFF' : '#000000',
        } as const;
    })();

    let renderSubMenus: SubMenu[] = [];
    if (renderMenu?.subMenusKey) {
        renderSubMenus = t(renderMenu.subMenusKey, {
            returnObjects: true,
        }) as SubMenu[];
    }

    let renderMenuDetails: MenuDetail[] = [];
    if (renderMenu?.dynamicDetails) {
        renderMenuDetails = blogMenuDetails;
    } else if (renderMenu?.menuDetailsKey) {
        renderMenuDetails = t(renderMenu.menuDetailsKey, {
            returnObjects: true,
        }) as MenuDetail[];
    }

    const overlayVisible = activeMenu || renderMenu !== null;

    return (
        <section className="fixed left-0 top-0 z-50 w-full">
            <header
                className={clsx(
                    'flex w-full flex-col items-center justify-center',
                    `text-${textColor}`,
                )}
            >
                {/* ── 桌面端导航栏 ── */}
                <div
                    style={{
                        paddingTop: 'var(--safe-top)',
                        paddingLeft: 'var(--safe-left)',
                        paddingRight: 'var(--safe-right)',
                    }}
                    className={clsx(
                        'j-l2 w-900:h-[56px] relative z-50 hidden h-[64px] w-full flex-row items-center justify-center',
                        'w-900:px-[24px] px-[20px] transition-all duration-100 sm:flex',
                    )}
                >
                    <div
                        className={clsx(
                            'pointer-events-none absolute inset-0 -z-10 max-w-full',
                            theme.navBg,
                            theme.navBorder,
                            theme.backdropBlur,
                        )}
                    ></div>
                    <div
                        className="relative flex h-full w-full max-w-[1488px] flex-row items-center justify-between px-[20px] sm:px-[40px]"
                        onMouseEnter={closePopover}
                    >
                        {/* 居中菜单列表 */}
                        <ul
                            className={clsx(
                                'absolute left-0 top-0 flex h-full w-full flex-row items-center justify-center',
                                theme.navListText,
                            )}
                        >
                            <li
                                className="h-full px-[18px]"
                                onMouseEnter={closePopover}
                            >
                                <Link
                                    href={`/${locale}`}
                                    className="flex h-full items-center justify-center"
                                >
                                    {t('home')}
                                </Link>
                            </li>

                            {POPOVER_MENUS.map((menu) => {
                                const isOpen = openPopover === menu.key;
                                return (
                                    <li
                                        key={menu.key}
                                        onMouseEnter={() => openMenu(menu.key)}
                                        className={clsx(
                                            'flex h-full flex-row items-center px-[18px]',
                                            isOpen
                                                ? theme.menuItemOpen
                                                : theme.menuItemClosed,
                                        )}
                                    >
                                        <MenuDropdownButton
                                            title={t(menu.titleKey)}
                                            dropdownOpen={isOpen}
                                        />
                                    </li>
                                );
                            })}
                        </ul>

                        {/* Logo（居左） */}
                        <Link
                            className="z-10 flex h-full flex-initial items-center"
                            href={`/${locale}`}
                        >
                            <Logo height={26} width={94} color={theme.logo} />
                        </Link>

                        {/* 右侧操作区 */}
                        <div className="z-10 flex h-full flex-initial items-center gap-[14px]">
                            <Suspense
                                fallback={
                                    <LoadingPlaceholder
                                        className={clsx(
                                            'h-[30px] w-[60px]',
                                            theme.text === 'white'
                                                ? 'bg-white/15'
                                                : 'bg-gray-200',
                                        )}
                                    />
                                }
                            >
                                <LanguageChanger
                                    textColor={theme.text}
                                    hoverBg={theme.langHoverBg}
                                    dropdownBg={theme.langDropdownBg}
                                    dropdownTextColor={
                                        (theme as any).langDropdownTextColor
                                    }
                                    dropdownBorder={theme.langDropdownBorder}
                                    dropdownShadow={theme.langDropdownShadow}
                                    dropdownItemHoverBg={
                                        theme.langDropdownItemHoverBg
                                    }
                                    onSelectorStateChange={
                                        setLanguageSelectorOpen
                                    }
                                />
                            </Suspense>

                            <FooterButton
                                icon={null}
                                withIcon={false}
                                textColor={theme.text}
                                textHoverColor={theme.contactHoverText}
                                borderColor={theme.text}
                                boderHoverColor={theme.text}
                                bgHoverColor={theme.text}
                                className="j-t4 h-[30px] hover:cursor-pointer"
                                text={t('contactUs')}
                                onClick={openForm}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Popover 面板 ── */}
                <div
                    id="popover-container"
                    className={clsx(
                        'pointer-events-none absolute left-0 top-[56px] z-40 hidden w-full overflow-hidden sm:block',
                        DESKTOP_POPOVER_HEIGHT_CLASS,
                        renderMenu ? 'opacity-100' : 'opacity-0',
                    )}
                >
                    <div
                        className={clsx(
                            'w-full origin-top transform-gpu transition-[opacity,transform] duration-[140ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform',
                            DESKTOP_POPOVER_HEIGHT_CLASS,
                            activeMenu
                                ? 'pointer-events-auto'
                                : 'pointer-events-none',
                            activeMenu
                                ? 'translate-y-0 opacity-100'
                                : '-translate-y-[8px] opacity-0',
                        )}
                    >
                        {renderMenu && (
                            <TopMenuPopover
                                key={renderMenu.key}
                                title={t(renderMenu.popoverTitleKey!)}
                                subMenus={renderSubMenus}
                                menuDetails={renderMenuDetails}
                                isDetailLoading={
                                    renderMenu.dynamicDetails
                                        ? isBlogMenuLoading
                                        : false
                                }
                                hide={closePopover}
                            />
                        )}
                    </div>
                </div>

                {/* ── 遮罩层：fixed 定位，独立于菜单动画 ── */}
                <div
                    className={clsx(
                        'fixed inset-0 top-[56px] z-30 transition-opacity duration-[140ms]',
                        overlayVisible
                            ? 'pointer-events-auto'
                            : 'pointer-events-none',
                        activeMenu ? 'opacity-100' : 'opacity-0',
                    )}
                    onMouseEnter={closePopover}
                    onClick={(e) => {
                        e.stopPropagation();
                        closePopover();
                    }}
                >
                    <div
                        aria-hidden="true"
                        className="bg-black/55 pointer-events-none absolute inset-0"
                    />
                </div>

                {/* ── 移动端导航栏 ── */}
                <div className="block h-[56px] w-full sm:hidden">
                    <TopMenu4Mobile
                        textColor={theme.text}
                        bgColor={menuActive ? 'white' : 'transparent'}
                        homeMenuText={t('home')}
                        productMenuText={t('product')}
                        companyMenuText={t('company')}
                        blogMenuText={t('blog')}
                        contactUsMenuText={t('contactUs')}
                        productTitle={t('productTitle')}
                        companyTitle={t('companyTitle')}
                        blogTitle={t('blogTitle')}
                        productSubMenus={
                            t('productSubMenus', {
                                returnObjects: true,
                            }) as SubMenu[]
                        }
                        companySubMenus={
                            t('companySubMenus', {
                                returnObjects: true,
                            }) as SubMenu[]
                        }
                        blogSubMenus={
                            t('blogSubMenus', {
                                returnObjects: true,
                            }) as SubMenu[]
                        }
                    />
                </div>
            </header>
        </section>
    );
}

export default TopMenu;
